import { AuthService } from "./auth.service";
import { RespondentAuditService } from "./respondent-audit.service";

export type RespondentFlowStep =
  | "ACCOUNT"
  | "VERIFY"
  | "PAYMENT"
  | "ENROLLMENT"
  | "RESCHEDULE"
  | "CLASS_COMPLETED"
  | "COURT_EXPORT"
  | "COMMUNICATION";

/** Strip accidental body/signature text from email subject lines. */
export function sanitizeEmailSubject(raw: string): string {
  let s = String(raw || "").trim();
  if (!s) return "";

  s = s.split(/\r?\n/)[0].trim();

  const cutPatterns = [
    /\s+Sincerely,?\s/i,
    /\s+Atentamente,?\s/i,
    /\s+SafeRoads Kane County Driver Program Coordinator/i,
    /\s+555 South Randall Road/i,
    /\s+info@saferoadskanecounty\.org/i,
  ];
  for (const pattern of cutPatterns) {
    const match = s.match(pattern);
    if (match?.index != null && match.index > 0) {
      s = s.slice(0, match.index).trim();
      break;
    }
  }

  return s;
}

/** Coerce audit metadata values to a positive enrollment id, or null when not numeric. */
export function normalizeEnrollmentId(value: unknown): number | null {
  if (typeof value === "number" && Number.isFinite(value) && value > 0) {
    return Math.trunc(value);
  }
  if (typeof value === "string") {
    const trimmed = value.trim();
    if (!/^\d+$/.test(trimmed)) return null;
    const parsed = Number.parseInt(trimmed, 10);
    return parsed > 0 ? parsed : null;
  }
  return null;
}

export type RespondentAuditWriteInput = {
  referenceId?: string;
  importId?: number | null;
  subjectUserId?: number | null;
  enrollmentId?: number | null;
  flowStep: RespondentFlowStep;
  eventLabel: string;
  eventType: string;
  outcome: "success" | "failed" | "submit";
  initiatedBy?: "student" | "admin" | "system";
  initiatedByName?: string | null;
  metadata?: Record<string, unknown> | null;
};

type CommAuditRow = {
  referenceId?: string;
  importId?: number;
  caseNumber?: string;
  ticketNumber?: string;
  name?: string;
  email?: string;
  template?: string;
  type?: string;
  userId?: number;
  enrollmentId?: unknown;
  messageBody?: string;
  messageSubject?: string;
};

function flowStepFromLabel(label: string): RespondentFlowStep | null {
  const l = String(label || "").toLowerCase();
  if (l.startsWith("account_create")) return "ACCOUNT";
  if (l.startsWith("verify_")) return "VERIFY";
  if (l.startsWith("payment_")) return "PAYMENT";
  if (l.startsWith("enroll_") || l.startsWith("admin_enroll_")) return "ENROLLMENT";
  if (l.startsWith("reschedule_") || l.startsWith("admin_enrollments_reschedule_")) return "RESCHEDULE";
  if (l.startsWith("switch_inperson_")) return "RESCHEDULE";
  if (l.startsWith("admin_attendance_")) return "CLASS_COMPLETED";
  if (l.startsWith("admin_course_completed_send_to_court")) return "COURT_EXPORT";
  if (l.startsWith("admin_comm_")) return "COMMUNICATION";
  return null;
}

function outcomeFromLabel(label: string, fallback?: "success" | "failed"): "success" | "failed" | "submit" {
  const l = String(label || "").toLowerCase();
  if (l.endsWith("_success") || l.endsWith("_completed")) return "success";
  if (l.endsWith("_failed")) return "failed";
  if (l.endsWith("_submit")) return "submit";
  return fallback ?? "success";
}

/** One respondent-audit row per comm recipient — strip bulk/shared preview fields. */
function buildPerRespondentCommAuditMetadata(
  baseMetadata: Record<string, unknown> | null | undefined,
  row: CommAuditRow,
  commRowCount: number,
): Record<string, unknown> {
  const baseMeta = baseMetadata ?? {};
  const baseInput: Record<string, unknown> = {
    ...(((baseMeta as { input?: Record<string, unknown> }).input) ?? {}),
  };
  const isBulkOperation = baseInput["bulk"] === "Yes" || commRowCount > 1;

  const {
    commRows: _commRows,
    commNote: _commNote,
    count: _count,
    successCount: _successCount,
    failureCount: _failureCount,
    totalCreated: _totalCreated,
    failedCount: _failedCount,
    messageBody: _messageBody,
    messageSubject: _messageSubject,
    referenceId: _referenceId,
    caseNumber: _caseNumber,
    ticketNumber: _ticketNumber,
    name: _name,
    email: _email,
    ...sharedInput
  } = baseInput;

  const perInput: Record<string, unknown> = {
    ...sharedInput,
    referenceId: row.referenceId,
    caseNumber: row.caseNumber,
    ticketNumber: row.ticketNumber,
    name: row.name,
    email: row.email,
    template: row.template ?? baseInput["template"],
    type: row.type ?? baseInput["type"],
    bulk: "No",
    commRows: [row],
  };

  if (row.messageSubject != null) {
    perInput["messageSubject"] = row.messageSubject;
  } else if (!isBulkOperation && baseInput["messageSubject"] != null) {
    perInput["messageSubject"] = baseInput["messageSubject"];
  }

  if (row.messageBody != null) {
    perInput["messageBody"] = row.messageBody;
  } else if (!isBulkOperation && baseInput["messageBody"] != null) {
    perInput["messageBody"] = baseInput["messageBody"];
  }

  const ref = String(row.referenceId || "").trim();
  const rowName = String(row.name || "").trim() || "Respondent";
  const baseContext = (baseMeta as { context?: Record<string, unknown> }).context ?? {};

  return {
    ...baseMeta,
    context: {
      ...baseContext,
      entityType: baseContext["entityType"] ?? "communication",
      entityId: ref,
      entityDisplay: `${rowName} (${ref})`,
    },
    input: perInput,
  };
}

function actorName(auth: AuthService, initiatedBy: string): string {
  if (initiatedBy === "system") return "System generated";
  const user = auth.getCurrentUser();
  if (!user) return initiatedBy === "student" ? "Student" : "Admin";
  const full = `${user.firstName || ""} ${user.lastName || ""}`.trim();
  return full || user.username || (initiatedBy === "student" ? "Student" : "Admin");
}

export function writeRespondentAudit(
  service: RespondentAuditService,
  auth: AuthService,
  input: RespondentAuditWriteInput,
): void {
  const referenceId = String(input.referenceId || "").trim();
  const importId =
    typeof input.importId === "number" && input.importId > 0
      ? Math.trunc(input.importId)
      : null;
  const enrollmentId = normalizeEnrollmentId(input.enrollmentId);
  if (!referenceId && !importId && !enrollmentId) return;

  const initiatedBy = input.initiatedBy ?? "admin";
  const payload = {
    referenceId: referenceId || undefined,
    importId,
    subjectUserId: input.subjectUserId ?? null,
    enrollmentId,
    flowStep: input.flowStep,
    eventLabel: input.eventLabel,
    eventType: input.eventType,
    outcome: input.outcome,
    initiatedBy,
    initiatedByName: input.initiatedByName ?? actorName(auth, initiatedBy),
    metadataJson: input.metadata ? JSON.stringify(input.metadata) : null,
  };

  service.createEvent(payload).subscribe({ error: () => {} });
}

/** Mirror an admin ACTION_ADMIN audit row into RespondentAuditEvents when we have a reference id. */
export function mirrorAdminAuditToRespondent(
  service: RespondentAuditService,
  auth: AuthService,
  label: string,
  eventType: string,
  options: {
    referenceId?: string | null;
    importId?: number | null;
    subjectUserId?: number | null;
    enrollmentId?: number | null;
    metadata?: Record<string, unknown> | null;
    outcome?: "success" | "failed" | "submit";
    initiatedBy?: "student" | "admin" | "system";
    flowStep?: RespondentFlowStep;
  },
): void {
  const flowStep = options.flowStep ?? flowStepFromLabel(label);
  if (!flowStep) return;

  const outcome = options.outcome ?? outcomeFromLabel(label);
  const labelLower = String(label || "").toLowerCase();
  const attendanceRows = (options.metadata as any)?.input?.attendanceRows;

  // Attendance saves: mirror one respondent-audit event per student (by reference id).
  if (
    labelLower.startsWith("admin_attendance_") &&
    (labelLower.endsWith("_success") || labelLower.endsWith("_failed")) &&
    Array.isArray(attendanceRows) &&
    attendanceRows.length > 0
  ) {
    const baseInput = { ...((options.metadata as any)?.input ?? {}) };
    for (const row of attendanceRows) {
      const ref = String(row?.referenceId || "").trim();
      const enrollmentId = normalizeEnrollmentId(row?.enrollmentId);
      if (!ref && !enrollmentId) continue;
      writeRespondentAudit(service, auth, {
        referenceId: ref,
        subjectUserId: options.subjectUserId ?? row?.userId ?? null,
        enrollmentId,
        flowStep,
        eventLabel: label,
        eventType,
        outcome,
        initiatedBy: options.initiatedBy ?? "admin",
        metadata: {
          ...(options.metadata ?? {}),
          input: { ...baseInput, attendanceRows: [row] },
        },
      });
    }
    return;
  }

  const courseCompletionRows = (options.metadata as any)?.input?.courseCompletionRows;

  // Send to court: mirror one respondent-audit event per exported record (by reference id).
  if (
    labelLower.startsWith("admin_course_completed_send_to_court_") &&
    (labelLower.endsWith("_success") || labelLower.endsWith("_failed")) &&
    Array.isArray(courseCompletionRows) &&
    courseCompletionRows.length > 0
  ) {
    const baseInput = { ...((options.metadata as any)?.input ?? {}) };
    for (const row of courseCompletionRows) {
      const ref = String(row?.referenceId || "").trim();
      if (!ref) continue;
      writeRespondentAudit(service, auth, {
        referenceId: ref,
        subjectUserId: options.subjectUserId ?? null,
        enrollmentId: normalizeEnrollmentId(row?.enrollmentId) ?? normalizeEnrollmentId(options.enrollmentId),
        flowStep,
        eventLabel: label,
        eventType,
        outcome,
        initiatedBy: options.initiatedBy ?? "admin",
        metadata: {
          ...(options.metadata ?? {}),
          input: { ...baseInput, courseCompletionRows: [row] },
        },
      });
    }
    return;
  }

  const commRows = (options.metadata as any)?.input?.commRows;
  if (flowStep === "COMMUNICATION" && Array.isArray(commRows) && commRows.length > 0) {
    for (const row of commRows) {
      const ref = String(row?.referenceId || "").trim();
      const rowImportId = Number(row?.importId || 0);
      if (!ref && rowImportId <= 0) continue;
      writeRespondentAudit(service, auth, {
        referenceId: ref || undefined,
        importId: rowImportId > 0 ? rowImportId : null,
        subjectUserId: options.subjectUserId ?? row?.userId ?? null,
        enrollmentId: normalizeEnrollmentId(row?.enrollmentId) ?? normalizeEnrollmentId(options.enrollmentId),
        flowStep,
        eventLabel: label,
        eventType,
        outcome,
        initiatedBy: options.initiatedBy ?? "admin",
        metadata: buildPerRespondentCommAuditMetadata(
          options.metadata,
          row as CommAuditRow,
          commRows.length,
        ),
      });
    }
    return;
  }

  const referenceId = String(options.referenceId || "").trim()
    || String((options.metadata as any)?.input?.referenceId || "").trim()
    || String((options.metadata as any)?.context?.referenceId || "").trim();
  const importIdRaw =
    options.importId
    ?? (options.metadata as any)?.input?.importId
    ?? (options.metadata as any)?.context?.importId;
  const importId =
    typeof importIdRaw === "number" && importIdRaw > 0
      ? Math.trunc(importIdRaw)
      : Number(importIdRaw) > 0
        ? Math.trunc(Number(importIdRaw))
        : null;
  if (!referenceId && !importId) return;

  writeRespondentAudit(service, auth, {
    referenceId: referenceId || undefined,
    importId,
    subjectUserId: options.subjectUserId
      ?? (options.metadata as any)?.context?.affectedUserId
      ?? (options.metadata as any)?.input?.userId
      ?? null,
    enrollmentId:
      normalizeEnrollmentId(options.enrollmentId)
      ?? normalizeEnrollmentId((options.metadata as any)?.input?.enrollmentId)
      ?? normalizeEnrollmentId((options.metadata as any)?.context?.entityId),
    flowStep,
    eventLabel: label,
    eventType,
    outcome,
    initiatedBy: options.initiatedBy ?? "admin",
    metadata: options.metadata ?? null,
  });
}
